/**
 * Three-point editing (1.7.4, docs/dev/editor-1.7.4-build.md): the sequence In and Out, the rule that resolves source
 * and sequence marks into one edit, track references and the source patch (where an edit's picture and sound go).
 * Pure; ops.overwriteEdit / ops.insertEdit take what these resolve.
 */
import { graphicTrackCount, sequenceDuration, type GraphicTrack, type SequenceDocument } from './sequence.ts';
import type { Span } from './sequence-ops.ts';

/** The marks a three-point edit starts from (ticks on the frame grid; null = not set). */
export interface ThreePointInput {
  /** The source viewer's In and Out, in source ticks. */
  sourceIn: number | null;
  sourceOut: number | null;
  /** The sequence In and Out (doc.marks), in record ticks. */
  seqIn: number | null;
  seqOut: number | null;
  /** The source's length (ticks); 0 or less when not known (a still): then it does not limit the edit. */
  sourceDuration: number;
  /** The playhead: the sequence In when neither sequence mark is set. */
  playhead: number;
}

/** One resolved edit: the source range and where it lands. */
export interface ThreePointEdit {
  sourceInTicks: number;
  sourceOutTicks: number;
  recordInTicks: number;
  recordOutTicks: number;
  /** The source had less media than the sequence In to Out asked for, so the edit is shorter than that range. */
  shortened: boolean;
}

/**
 * Resolve the marks into one edit (the plan's three-point rule: any three of source In/Out and sequence In/Out set the
 * fourth; without sequence marks the playhead is the sequence In; over-specified marks use the source duration).
 * A source mark left out is the source's start (In) or end (Out); "set" below means set by the user.
 *  - No sequence mark: the source In–Out at the playhead.
 *  - Sequence In only: the source In–Out at the sequence In.
 *  - Sequence Out only: the source In–Out ending at the sequence Out (backtimed); when that would start before 0, the
 *    source In moves later so it starts at 0 (shortened).
 *  - Sequence In and Out (length L):
 *    - both source marks set: over-specified, the source In–Out at the sequence In (the source duration wins; the
 *      sequence Out is not used);
 *    - only the source Out set: backtimed, the source In is Out − L; when that is before the source's start, the edit
 *      starts at 0 in the source and ends at the sequence Out (shortened);
 *    - otherwise: from the source In (or its start), L long; when the source ends first, up to its end (shortened).
 * Null when the edit would be empty (no media in the range).
 */
export function resolveThreePoint(m: ThreePointInput): ThreePointEdit | null {
  const known = m.sourceDuration > 0 && Number.isFinite(m.sourceDuration);
  const end = known ? m.sourceDuration : Number.POSITIVE_INFINITY;
  let sIn = Math.max(0, Math.min(m.sourceIn ?? 0, end));
  let sOut = m.sourceOut ?? end;
  sOut = Math.min(sOut, end);
  let shortened = false;
  let rIn: number;
  const seqIn = m.seqIn ?? null, seqOut = m.seqOut ?? null;
  if (seqIn !== null && seqOut !== null && seqOut > seqIn) {
    const L = seqOut - seqIn;
    if (m.sourceIn !== null && m.sourceOut !== null) rIn = seqIn;
    else if (m.sourceOut !== null) {
      sIn = sOut - L;
      if (sIn < 0) { sIn = 0; shortened = true; }
      rIn = seqOut - (sOut - sIn);
    } else {
      if (sIn + L > end) { sOut = end; shortened = true; } else sOut = sIn + L;
      rIn = seqIn;
    }
  } else if (seqIn === null && seqOut !== null) {
    if (!Number.isFinite(sOut)) return null;
    rIn = seqOut - (sOut - sIn);
    if (rIn < 0) { sIn += -rIn; rIn = 0; shortened = true; }
  } else rIn = Math.max(0, seqIn ?? m.playhead);
  if (!Number.isFinite(sOut) || sOut <= sIn) return null;
  return { sourceInTicks: sIn, sourceOutTicks: sOut, recordInTicks: rIn, recordOutTicks: rIn + (sOut - sIn), shortened };
}

/**
 * The sequence's In to Out (1.7.4) as a span: the In (or 0) to the Out (or the sequence's end); null when no mark is
 * set (then "In to Out" is the whole sequence). What the export's, the EDL's and Send to edit's "In to Out" use.
 */
export function markRange(doc: SequenceDocument): Span | null {
  const m = doc.marks;
  if (!m || (m.inTicks === undefined && m.outTicks === undefined)) return null;
  const start = m.inTicks ?? 0;
  const end = m.outTicks ?? sequenceDuration(doc);
  return end > start ? { start, end } : null;
}

/* ---------------------------------------------------------------- tracks and the source patch */

/**
 * A track as the editing ops name it (1.7.4): V1, a layer (V2…), an audio track (A2: id 'a2', or an audioTracks
 * id) or a graphics track (counted from 0). A1 is not a track of its own here: it is the V1 clips' sound.
 */
export type TrackRef =
  | { kind: 'v1' }
  | { kind: 'layer'; id: string }
  | { kind: 'audio'; id: string }
  | { kind: 'graphic'; track: GraphicTrack };

/** A stable key for a track: 'v1', 'layer:<id>', 'audio:<id>', 'graphic:<0|1|2>'. */
export const trackKey = (t: TrackRef): string => (t.kind === 'v1' ? 'v1' : t.kind === 'graphic' ? `graphic:${t.track}` : `${t.kind}:${t.id}`);

/** Every track of the document: V1, the layers (V2 up), A2…A8, then GFX 1…graphicTrackCount. */
export function allTrackRefs(doc: SequenceDocument): TrackRef[] {
  const graphicTracks = graphicTrackCount(doc);
  return [
    { kind: 'v1' },
    ...(doc.layers ?? []).map((l) => ({ kind: 'layer' as const, id: l.id })),
    { kind: 'audio', id: 'a2' },
    ...(doc.audioTracks ?? []).map((t) => ({ kind: 'audio' as const, id: t.id })),
    ...Array.from({ length: graphicTracks }, (_, i) => ({ kind: 'graphic' as const, track: i as GraphicTrack })),
  ];
}

/**
 * The source patch (1.7.4 track targeting): where a three-point edit (or the source viewer's Insert, Overwrite) puts
 * the source's picture and sound.
 *  - video: 'v1', a layer's id ({ layer }), or null (no picture: a sound-only edit);
 *  - audio: 'with-picture' (the clip's own sound: A1 on V1; 1.8.0, on a layer, that layer's audio track, V2 → A2), an audio track
 *    ({ track: 'a2' or an audioTracks id }: the picture lands muted and its sound is an audio clip there, linked to
 *    it), or null (no sound: the picture lands muted).
 * 'with-picture' with no picture is no edit. The editor keeps it per sequence (docs/dev/editor-1.7.4-build.md).
 */
export interface EditTargets {
  video: 'v1' | { layer: string } | null;
  audio: 'with-picture' | { track: string } | null;
}
export const DEFAULT_EDIT_TARGETS: EditTargets = { video: 'v1', audio: 'with-picture' };

/**
 * The targets as they apply to this document: a layer or audio track that no longer exists falls back to the
 * default ('v1', 'with-picture'), so a stale pref never makes an edit vanish.
 */
export function resolveTargets(doc: SequenceDocument, t: EditTargets | null | undefined): EditTargets {
  if (!t) return DEFAULT_EDIT_TARGETS;
  const video = t.video && t.video !== 'v1' && !(doc.layers ?? []).some((l) => l.id === (t.video as { layer: string }).layer) ? 'v1' : t.video;
  const audio = t.audio && t.audio !== 'with-picture' && t.audio.track !== 'a2' && !(doc.audioTracks ?? []).some((x) => x.id === (t.audio as { track: string }).track) ? 'with-picture' : t.audio;
  return video === t.video && audio === t.audio ? t : { video, audio };
}
